/**
 * PASO 1 — Servicios (extracted from pages/BookingFlow.js).
 */
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Scissors, Play, Images, Plus, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDuration } from '@/utils/bookingFormat';
import { ServiceGallery, serviceCover } from '@/components/Booking/ServiceGallery';
import { tr } from '@/contexts/LanguageContext';
import { hasCardPrice, bothPrices } from '@/utils/cardPricing';

// LOTE 3 (founder 14-oct-2026) — varios servicios: «Reservar» = uno solo (como siempre); «+» agrega a la lista.
export const mergeServices = (list) => (list.length === 1 ? { ...list[0], ids: [list[0].id], lines: list } : {
  id: list[0].id, ids: list.map((x) => x.id), lines: list,
  name: list.map((x) => x.name).join(' + '),
  price: Math.round(list.reduce((a, x) => a + Number(x.price || 0), 0) * 100) / 100,
  cash_price: list.some((x) => x.cash_price != null) ? Math.round(list.reduce((a, x) => a + Number(x.cash_price ?? x.price ?? 0), 0) * 100) / 100 : undefined,
  card_price: list.some((x) => x.card_price != null) ? Math.round(list.reduce((a, x) => a + Number(x.card_price ?? x.price ?? 0), 0) * 100) / 100 : undefined,
  duration_minutes: list.reduce((a, x) => a + Number(x.duration_minutes || 0), 0),
  premium: list.some((x) => x.premium),
});

export const ServicesStep = ({ barber, services, search, onSearch, onSelect }) => {
  const [picked, setPicked] = useState([]);
  const toggle = (svc) => setPicked((p) => (p.some((x) => x.id === svc.id) ? p.filter((x) => x.id !== svc.id) : (p.length >= 6 ? p : [...p, svc])));
  const merged = picked.length ? mergeServices(picked) : null;
  const [gallery, setGallery] = useState(null);
  const filtered = useMemo(() => {
    if (!services) return null;
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q)
    );
  }, [services, search]);

  if (services === null) {
    return (
      <div className="py-12 text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-14 bg-card border border-border rounded-2xl" data-testid="booking-services-empty">
        <Scissors className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-display text-lg font-bold text-foreground mb-1">{tr("Sin servicios disponibles")}</h3>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">{tr("Este barbero aún no tiene servicios disponibles para agendar.")}</p>
      </div>
    );
  }

  const searching = search.trim().length > 0;
  const popular = searching ? [] : (filtered || []).slice(0, 3);
  const rest = searching ? (filtered || []) : (filtered || []).slice(3);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="booking-step-services">
      <div className="relative mb-5">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={tr("Buscar servicios")}
          className="pl-10 h-11 rounded-xl bg-card border-border"
          data-testid="booking-services-search"
        />
      </div>

      {!searching && popular.length > 0 && (
        <ServiceSection title={tr("Servicios más populares")} items={popular} onSelect={onSelect} onGallery={setGallery} picked={picked} onToggle={toggle} />
      )}
      {rest.length > 0 && (
        <ServiceSection title={searching ? tr("Resultados") : tr("Otros servicios")} items={rest} onSelect={onSelect} onGallery={setGallery} picked={picked} onToggle={toggle} />
      )}
      {searching && rest.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="booking-services-no-results">{tr("Sin resultados para “{0}”.", search)}</p>
      )}
      {gallery && <ServiceGallery service={gallery} startIndex={gallery.cover_index || 0} onClose={() => setGallery(null)} />}
      {merged && (
        <div className="sticky bottom-3 z-30 mt-4" data-testid="booking-multi-bar">
          <div className="rounded-2xl bg-card/95 backdrop-blur border border-primary/40 shadow-xl p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground" data-testid="booking-multi-count">{tr("{0} servicios", picked.length)} · {formatDuration(merged.duration_minutes)}</p>
              <p className="text-sm font-semibold truncate" data-testid="booking-multi-names">{merged.name}</p>
            </div>
            <p className="font-display font-bold text-primary text-lg" data-testid="booking-multi-total">${merged.price.toFixed(2)}</p>
            <Button onClick={() => onSelect(merged)} className="h-10 px-4 rounded-full gap-1.5" data-testid="booking-multi-continue">
              {tr("Continuar")} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// 1D — portada del servicio (solo si tiene fotos/videos); toca para abrir la galería.
const ServiceCoverThumb = ({ svc, onOpen }) => {
  const cover = serviceCover(svc);
  if (!cover) return null;
  const count = (svc.media || []).length;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(svc); }}
      className="relative w-14 h-14 rounded-xl overflow-hidden border border-border bg-black shrink-0 active:scale-95 transition-transform"
      aria-label={tr("Ver fotos de {0}", svc.name)} data-testid={`booking-service-cover-${svc.id}`}>
      {cover.src ? <img src={cover.src} alt="" className="w-full h-full object-cover" />
        : <video src={cover.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />}
      {cover.kind === 'video' && <Play className="absolute bottom-1 right-1 w-3.5 h-3.5 text-white drop-shadow" fill="currentColor" />}
      {count > 1 && (
        <span className="absolute top-0.5 left-0.5 rounded-full bg-black/60 text-white text-[9px] font-bold px-1 inline-flex items-center gap-0.5">
          <Images className="w-2.5 h-2.5" />{count}
        </span>
      )}
    </button>
  );
};

const ServiceSection = ({ title, items, onSelect, onGallery, picked = [], onToggle }) => (
  <div className="mb-6">
    <h3 className="font-display text-sm font-bold text-primary uppercase tracking-widest mb-2 px-0.5">{title}</h3>
    <div className="bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden">
      {items.map((svc) => (
        <div key={svc.id} className="flex items-center gap-3 p-4" data-testid={`booking-service-${svc.id}`}>
          <ServiceCoverThumb svc={svc} onOpen={onGallery} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground text-sm truncate">{svc.name}</p>
            {(svc.description || svc.category) && (
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {svc.description || svc.category}
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            {hasCardPrice(svc) ? (
              <p className="font-display font-bold text-primary text-xs leading-tight whitespace-nowrap" data-testid={`svc-both-prices-${svc.id}`}>{bothPrices(svc.cash_price ?? svc.price, svc.card_price)}</p>
            ) : (
              <p className="font-display font-bold text-primary text-base leading-tight">${Number(svc.price).toFixed(0)}</p>
            )}
            <p className="text-[11px] text-muted-foreground">{formatDuration(svc.duration_minutes)}</p>
          </div>
          {picked.length === 0 && (
            <Button
              onClick={() => onSelect(svc)}
              className="h-9 px-4 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold shrink-0"
              data-testid={`booking-service-select-${svc.id}`}
            >{tr("Reservar")}</Button>
          )}
          <button
            type="button"
            onClick={() => onToggle(svc)}
            aria-label={picked.some((x) => x.id === svc.id) ? tr("Quitar servicio") : tr("Añadir un servicio")}
            className={`h-9 w-9 rounded-full border flex items-center justify-center shrink-0 transition-colors ${picked.some((x) => x.id === svc.id) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-primary hover:bg-primary/10'}`}
            data-testid={`booking-service-add-${svc.id}`}
          >{picked.some((x) => x.id === svc.id) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</button>
        </div>
      ))}
    </div>
  </div>
);
